import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Wand2,
  Calendar,
  BarChart3,
  TrendingUp,
  Clock,
  Eye,
  Instagram,
  Facebook,
  ChevronRight,
  Check,
  Loader2,
  Rocket
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export const OnboardingPage = ({ user, token, onComplete }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [loading, setLoading] = useState(false);

  const steps = [
    { id: "welcome", title: "Bienvenida" },
    { id: "features", title: "Funciones" },
    { id: "goals", title: "Objetivos" },
    { id: "connect", title: "Conectar" }
  ];

  const goals = [
    {
      id: "grow",
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Crecer mi audiencia",
      description: "Aumentar seguidores y alcance en mis redes sociales"
    },
    {
      id: "save_time",
      icon: <Clock className="w-6 h-6" />,
      title: "Ahorrar tiempo",
      description: "Programar contenido y automatizar publicaciones"
    },
    {
      id: "analyze_competition",
      icon: <Eye className="w-6 h-6" />,
      title: "Analizar competencia",
      description: "Monitorear competidores y descubrir tendencias"
    }
  ];

  const platforms = [
    {
      id: "instagram",
      name: "Instagram",
      icon: <Instagram className="w-8 h-8" />,
      color: "bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400"
    },
    {
      id: "tiktok",
      name: "TikTok",
      icon: <TikTokIcon className="w-8 h-8" />,
      color: "bg-black"
    },
    {
      id: "facebook",
      name: "Facebook",
      icon: <Facebook className="w-8 h-8" />,
      color: "bg-blue-600"
    }
  ];

  const features = [
    {
      icon: <Wand2 className="w-12 h-12 text-primary" />,
      title: "Creación con IA",
      description: "Genera contenido atractivo, hashtags y captions optimizados con inteligencia artificial"
    },
    {
      icon: <Calendar className="w-12 h-12 text-primary" />,
      title: "Programación Inteligente",
      description: "Planifica y programa tus publicaciones en el mejor momento para maximizar el engagement"
    },
    {
      icon: <BarChart3 className="w-12 h-12 text-primary" />,
      title: "Analíticas Unificadas",
      description: "Visualiza el rendimiento de todas tus redes en un solo dashboard centralizado"
    }
  ];

  const [featureIndex, setFeatureIndex] = useState(0);

  const handleGoalToggle = (goalId) => {
    setSelectedGoals(prev => 
      prev.includes(goalId)
        ? prev.filter(g => g !== goalId)
        : [...prev, goalId]
    );
  };

  const handleComplete = async () => {
    if (selectedGoals.length === 0) {
      toast.error("Selecciona al menos un objetivo");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/onboarding/complete`,
        {
          goals: selectedGoals,
          first_platform: selectedPlatform
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success("¡Bienvenido a StratIA!");
      onComplete();
      navigate("/dashboard");
    } catch (error) {
      console.error("Error completing onboarding:", error);
      toast.error("Error al completar el onboarding");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/onboarding/complete`,
        {
          goals: selectedGoals.length > 0 ? selectedGoals : ["grow"],
          first_platform: null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      onComplete();
      navigate("/dashboard");
    } catch (error) {
      console.error("Error skipping onboarding:", error);
      toast.error("Error al completar");
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const nextFeature = () => {
    if (featureIndex < features.length - 1) {
      setFeatureIndex(prev => prev + 1);
    } else {
      nextStep();
    }
  };

  const renderWelcome = () => (
    <div className="text-center space-y-8 animate-in fade-in duration-500">
      {/* Logo Animation */}
      <div className="relative">
        <div className="w-24 h-24 mx-auto bg-primary rounded-2xl flex items-center justify-center shadow-lg shadow-primary/30 animate-pulse">
          <Sparkles className="w-12 h-12 text-primary-foreground" />
        </div>
        <div className="absolute -top-2 -right-2 w-32 h-32 bg-accent/20 rounded-full blur-2xl" />
      </div>
      
      <div className="space-y-4">
        <h1 className="text-4xl sm:text-5xl font-bold">
          ¡Hola, {user?.name?.split(' ')[0]}!
        </h1>
        <p className="text-xl text-muted-foreground max-w-md mx-auto">
          Bienvenido a <span className="text-primary font-semibold">StratIA</span>
        </p>
      </div>
      
      <div className="bg-secondary/50 rounded-2xl p-8 max-w-lg mx-auto">
        <h2 className="text-2xl font-semibold mb-4">
          Gestiona todas tus redes en un solo lugar
        </h2>
        <p className="text-muted-foreground">
          Programa, crea con IA y analiza el rendimiento de tu contenido en Instagram, TikTok, Facebook y más.
        </p>
      </div>
      
      <Button 
        size="lg" 
        onClick={nextStep}
        className="gap-2 px-8"
        data-testid="onboarding-start-btn"
      >
        Comenzar
        <ArrowRight className="w-5 h-5" />
      </Button>
    </div>
  );

  const renderFeatures = () => (
    <div className="text-center space-y-8 animate-in fade-in duration-500">
      {/* Progress Dots */}
      <div className="flex justify-center gap-2">
        {features.map((_, idx) => (
          <div 
            key={idx}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === featureIndex ? 'bg-primary w-8' : 'bg-muted'
            }`}
          />
        ))}
      </div>
      
      {/* Feature Card */}
      <div className="min-h-[300px] flex items-center justify-center">
        <div className="space-y-6 max-w-md mx-auto animate-in slide-in-from-right duration-300" key={featureIndex}>
          <div className="w-24 h-24 mx-auto bg-primary/10 rounded-2xl flex items-center justify-center">
            {features[featureIndex].icon}
          </div>
          <h2 className="text-3xl font-bold">
            {features[featureIndex].title}
          </h2>
          <p className="text-lg text-muted-foreground">
            {features[featureIndex].description}
          </p>
        </div>
      </div>
      
      <div className="flex justify-center gap-4">
        {featureIndex > 0 && (
          <Button 
            variant="outline" 
            onClick={() => setFeatureIndex(prev => prev - 1)}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Anterior
          </Button>
        )}
        <Button 
          onClick={nextFeature}
          className="gap-2 px-8"
          data-testid="onboarding-next-feature-btn"
        >
          {featureIndex === features.length - 1 ? "Continuar" : "Siguiente"}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  const renderGoals = () => (
    <div className="text-center space-y-8 animate-in fade-in duration-500">
      <div className="space-y-2">
        <h2 className="text-3xl font-bold">¿Qué quieres lograr?</h2>
        <p className="text-muted-foreground">
          Selecciona tus objetivos y personalizaremos tu experiencia
        </p>
      </div>
      
      <div className="grid gap-4 max-w-lg mx-auto">
        {goals.map((goal) => {
          const isSelected = selectedGoals.includes(goal.id);
          return (
            <Card 
              key={goal.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'border-primary bg-primary/5 shadow-md' : ''
              }`}
              onClick={() => handleGoalToggle(goal.id)}
              data-testid={`goal-${goal.id}`}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  isSelected ? 'bg-primary text-primary-foreground' : 'bg-secondary'
                }`}>
                  {goal.icon}
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold">{goal.title}</h3>
                  <p className="text-sm text-muted-foreground">{goal.description}</p>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  isSelected ? 'bg-primary border-primary' : 'border-muted'
                }`}>
                  {isSelected && <Check className="w-4 h-4 text-primary-foreground" />}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      <div className="flex justify-center gap-4">
        <Button 
          variant="outline" 
          onClick={prevStep}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Anterior
        </Button>
        <Button 
          onClick={nextStep}
          disabled={selectedGoals.length === 0}
          className="gap-2 px-8"
          data-testid="onboarding-goals-next-btn"
        >
          Continuar
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  const renderConnect = () => (
    <div className="text-center space-y-8 animate-in fade-in duration-500">
      <div className="space-y-2">
        <h2 className="text-3xl font-bold">Conecta tu primera red</h2>
        <p className="text-muted-foreground">
          Elige la plataforma donde quieres empezar a crear contenido
        </p>
      </div>
      
      <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
        {platforms.map((platform) => {
          const isSelected = selectedPlatform === platform.id;
          return (
            <Card 
              key={platform.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'ring-2 ring-primary shadow-lg' : ''
              }`}
              onClick={() => setSelectedPlatform(platform.id)}
              data-testid={`platform-${platform.id}`}
            >
              <CardContent className="p-6 flex flex-col items-center gap-3">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-white ${platform.color}`}>
                  {platform.icon}
                </div>
                <span className="font-medium">{platform.name}</span>
                {isSelected && (
                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-3 h-3 text-primary-foreground" />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      <p className="text-sm text-muted-foreground">
        Podrás añadir más redes sociales después desde la configuración
      </p>
      
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <Button 
          variant="outline" 
          onClick={prevStep}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Anterior
        </Button>
        <Button 
          variant="ghost"
          onClick={handleSkip}
          disabled={loading}
        >
          Saltar por ahora
        </Button>
        <Button 
          onClick={handleComplete}
          disabled={loading}
          className="gap-2 px-8"
          data-testid="onboarding-complete-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Rocket className="w-4 h-4" />
              {selectedPlatform ? "Empezar con " + platforms.find(p => p.id === selectedPlatform)?.name : "Empezar"}
            </>
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="onboarding-page">
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>
      
      <div className="relative z-10 w-full max-w-2xl">
        {/* Step indicators */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            {steps.map((step, idx) => (
              <div key={step.id} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  idx === currentStep 
                    ? 'bg-primary text-primary-foreground' 
                    : idx < currentStep 
                      ? 'bg-primary/20 text-primary' 
                      : 'bg-muted text-muted-foreground'
                }`}>
                  {idx < currentStep ? <Check className="w-4 h-4" /> : idx + 1}
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-12 h-1 mx-1 rounded ${
                    idx < currentStep ? 'bg-primary/50' : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <Card className="border-0 shadow-xl bg-card/80 backdrop-blur">
          <CardContent className="p-8 sm:p-12">
            {currentStep === 0 && renderWelcome()}
            {currentStep === 1 && renderFeatures()}
            {currentStep === 2 && renderGoals()}
            {currentStep === 3 && renderConnect()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
